import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  AlertOctagon,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Boxes,
  CircleDollarSign,
  ClipboardList,
  HandCoins,
  Info,
  ListChecks,
  RefreshCw,
  ShoppingCart,
  Store,
  TrendingUp,
  Truck,
  Users,
  Warehouse,
} from "lucide-react";

import StockManagerLayout from "../components/StockManagerLayout";
import { API_BASE_URL } from "../utils/apiConfig";

const SERIES = { retail: "#2a78d6", b2b: "#eb6834" };

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const count = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });

const money = (value) => inr.format(Number(value || 0));
const num = (value) => count.format(Number(value || 0));
const plural = (n, word, many = `${word}s`) => `${num(n)} ${Number(n) === 1 ? word : many}`;

// ₹1.2L / ₹3.4Cr for axis labels and tight spaces.
const compactMoney = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(abs >= 1e8 ? 0 : 1)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(abs >= 1e6 ? 0 : 1)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}K`;
  return `₹${Math.round(n)}`;
};

const shortDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" });
};

const dayLabel = (key) =>
  new Date(`${key}T12:00:00+05:30`).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });

const PERIODS = [
  { days: 1, label: "Today" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

const CHANNEL_LABELS = {
  RETAIL: "Outlet counter (POS)",
  ONLINE: "Online orders",
  B2B: "Dispatch to buyers",
  INTERNAL: "Transfers to own outlets",
};

// ---------- small building blocks ----------

const Delta = ({ value, inverse = false, suffix = "vs previous period" }) => {
  if (value === null || value === undefined) {
    return <span className="text-xs text-slate-500">New this period</span>;
  }
  const up = value > 0;
  const flat = value === 0;
  const good = inverse ? !up : up;
  const tone = flat ? "bg-slate-100 text-slate-600" : good ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700";
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
      <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 font-semibold ${tone}`}>
        {!flat && <Icon size={12} />}
        {Math.abs(value)}%
      </span>
      {suffix}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, delta, deltaInverse, lines = [], loading }) => (
  <div className="flex flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <Icon size={18} className="text-slate-400" />
    </div>
    <p className={`mt-2 text-2xl font-bold text-slate-900 ${loading ? "animate-pulse text-slate-300" : ""}`}>{value}</p>
    <div className="mt-1 min-h-[20px]">{delta !== undefined && <Delta value={delta} inverse={deltaInverse} />}</div>
    {lines.filter(Boolean).map((line) => (
      <p key={line} className="mt-1 text-xs text-slate-500">{line}</p>
    ))}
  </div>
);

const Panel = ({ title, icon: Icon, subtitle, children, action, className = "" }) => (
  <section className={`min-w-0 rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}>
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
      <div>
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          {Icon && <Icon size={17} className="text-slate-500" />}
          {title}
        </h2>
        {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const Empty = ({ children = "Nothing to show for this period." }) => (
  <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">{children}</div>
);

const Table = ({ columns, rows, empty }) => {
  if (!rows?.length) return <Empty>{empty}</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs uppercase text-slate-500">
            {columns.map((c) => (
              <th key={c.key} className={`whitespace-nowrap px-2 py-2 font-semibold ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-slate-100 last:border-0">
              {columns.map((c) => (
                <td key={c.key} className={`px-2 py-2 text-slate-700 ${c.right ? "whitespace-nowrap text-right tabular-nums" : ""}`}>
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MarginBadge = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-slate-400">—</span>;
  const tone = value < 0 ? "bg-rose-50 text-rose-700" : value < 5 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
  return <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${tone}`}>{value}%</span>;
};

const ProductCell = ({ row }) => (
  <div>
    <p className="font-medium text-slate-800">{row.name}</p>
    <p className="text-xs text-slate-500">{[row.brand, row.pack].filter(Boolean).join(" · ")}</p>
  </div>
);

const ShareBars = ({ rows, valueKey = "amount", labelKey = "label", color = SERIES.retail, format = money }) => {
  const max = Math.max(...rows.map((r) => Number(r[valueKey] || 0)), 1);
  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div key={row[labelKey]}>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-slate-700">{row[labelKey]}</span>
            <span className="tabular-nums font-semibold text-slate-900">
              {format(row[valueKey])}
              {row.sharePercent !== undefined && <span className="ml-1 text-xs font-normal text-slate-500">{row.sharePercent}%</span>}
            </span>
          </div>
          <div className="mt-1 h-2 rounded-full bg-slate-100">
            <div className="h-2 rounded-full" style={{ width: `${Math.max(2, (Number(row[valueKey] || 0) / max) * 100)}%`, background: row.color || color }} />
          </div>
          {row.note && <p className="mt-0.5 text-xs text-slate-500">{row.note}</p>}
        </div>
      ))}
    </div>
  );
};

// ---------- sales trend chart ----------

const TrendChart = ({ rows }) => {
  const [hover, setHover] = useState(null);
  const width = 760;
  const height = 240;
  const pad = { top: 12, right: 8, bottom: 26, left: 52 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(...rows.map((r) => r.total), 1);
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const niceMax = Math.ceil(max / step) * step;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * niceMax);
  const slot = innerW / Math.max(rows.length, 1);
  const barW = Math.max(2, Math.min(36, slot * 0.7));
  const y = (v) => pad.top + innerH - (v / niceMax) * innerH;
  const labelEvery = Math.ceil(rows.length / 8);
  const active = hover !== null ? rows[hover] : null;

  if (!rows.length || !rows.some((r) => r.total > 0)) return <Empty>No sales recorded in this period.</Empty>;

  return (
    <div className="relative">
      <div className="mb-2 flex flex-wrap gap-4 text-xs text-slate-600">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: SERIES.retail }} />Outlet &amp; online</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: SERIES.b2b }} />Dispatch to buyers</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Daily net sales" onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth="1" />
            <text x={pad.left - 6} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#64748b">{compactMoney(t)}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          const x = pad.left + i * slot + (slot - barW) / 2;
          const retailTop = y(r.retail);
          const b2bTop = y(r.retail + r.b2b);
          const gap = r.retail > 0 && r.b2b > 0 ? 2 : 0;
          return (
            <g key={r.date} onMouseEnter={() => setHover(i)} onClick={() => setHover(i)}>
              <rect x={pad.left + i * slot} y={pad.top} width={slot} height={innerH} fill={hover === i ? "#f1f5f9" : "transparent"} />
              {r.retail > 0 && <rect x={x} y={retailTop} width={barW} height={y(0) - retailTop} fill={SERIES.retail} rx={r.b2b > 0 ? 0 : Math.min(4, barW / 2)} />}
              {r.b2b > 0 && <rect x={x} y={b2bTop} width={barW} height={Math.max(0, retailTop - b2bTop - gap)} fill={SERIES.b2b} rx={Math.min(4, barW / 2)} />}
              {i % labelEvery === 0 && (
                <text x={x + barW / 2} y={height - 8} textAnchor="middle" fontSize="11" fill="#64748b">{dayLabel(r.date)}</text>
              )}
            </g>
          );
        })}
      </svg>
      {active && (
        <div className="pointer-events-none absolute right-2 top-8 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
          <p className="font-semibold text-slate-900">{dayLabel(active.date)}</p>
          <p className="text-slate-600">Outlet &amp; online: <b className="text-slate-900">{money(active.retail)}</b></p>
          <p className="text-slate-600">Dispatch: <b className="text-slate-900">{money(active.b2b)}</b></p>
          <p className="text-slate-600">Gross profit: <b className="text-slate-900">{money(active.profit)}</b></p>
          <p className="text-slate-600">Bills: <b className="text-slate-900">{num(active.orders)}</b></p>
        </div>
      )}
    </div>
  );
};

// ---------- page ----------

const ALERT_STYLE = {
  critical: { icon: AlertOctagon, box: "border-rose-200 bg-rose-50", text: "text-rose-800", label: "Urgent" },
  warning: { icon: AlertTriangle, box: "border-amber-200 bg-amber-50", text: "text-amber-800", label: "Attention" },
  info: { icon: Info, box: "border-sky-200 bg-sky-50", text: "text-sky-800", label: "Note" },
};

export default function DirectorDashboardPage() {
  const token = useSelector((state) => state.posUser?.userInfo?.token);
  const [days, setDays] = useState(7);
  const [outlet, setOutlet] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [productView, setProductView] = useState("topByRevenue");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (outlet) params.set("outlet", outlet);
      const response = await fetch(`${API_BASE_URL}/inventory-dashboard/director?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.message || "Could not load the dashboard.");
      setData(body);
    } catch (err) {
      setError(err.message || "Could not load the dashboard.");
    } finally {
      setLoading(false);
    }
  }, [token, days, outlet]);

  useEffect(() => {
    load();
  }, [load]);

  const k = data?.kpis || {};
  const outlets = data?.filters?.outlets || [];
  const channels = data?.channels || {};

  const channelRows = useMemo(
    () =>
      ["RETAIL", "ONLINE", "B2B", "INTERNAL"].map((key) => ({
        label: CHANNEL_LABELS[key],
        amount: channels[key]?.revenue || 0,
        color: key === "B2B" ? SERIES.b2b : key === "INTERNAL" ? "#94a3b8" : SERIES.retail,
        note:
          key === "INTERNAL"
            ? "Not counted in net sales — it is stock moving inside the business."
            : channels[key]?.orders
            ? `${plural(channels[key].orders, "bill")} · margin ${channels[key].marginPercent ?? "—"}%`
            : "",
      })),
    [channels]
  );

  const productRows = data?.products?.[productView] || [];
  const warehouse = data?.stock?.warehouse;
  const receivables = data?.receivables;
  const purchases = data?.purchases;
  const customers = data?.customers;
  const profit = k.grossProfit || {};
  const rangeText = data
    ? `${shortDate(data.range.from)} – ${shortDate(data.range.to)}, compared with ${shortDate(data.range.previousFrom)} – ${shortDate(data.range.previousTo)}`
    : "";

  return (
    <StockManagerLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-950">Business Overview</h1>
            <p className="mt-1 text-sm text-slate-500">{rangeText || "Sales, profit, money to collect and stock health across the business."}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
              {PERIODS.map((p) => (
                <button
                  key={p.days}
                  onClick={() => setDays(p.days)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold ${days === p.days ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <select
              value={outlet}
              onChange={(e) => setOutlet(e.target.value)}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none focus:border-sky-500"
              aria-label="Outlet"
            >
              <option value="">All outlets</option>
              {outlets.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
              {outlet && !outlets.includes(outlet) && <option value={outlet}>{outlet}</option>}
            </select>
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-slate-800 px-3 text-sm font-bold text-white hover:bg-slate-900 disabled:bg-slate-400"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              {loading ? "Loading" : "Refresh"}
            </button>
            <Link to="/inventory/dashboard/operations" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              <ListChecks size={15} /> Details
            </Link>
          </div>
        </div>

        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</div>}
        {data?.unavailableSections?.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            Some figures could not be loaded: {data.unavailableSections.join(", ")}.
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <KpiCard icon={TrendingUp} label="Net sales" loading={loading && !data} value={money(k.netSales?.value)} delta={k.netSales?.changePercent}
            lines={[data && `Previous: ${money(k.netSales?.previous)}`]} />
          <KpiCard icon={CircleDollarSign} label="Gross profit" loading={loading && !data} value={money(profit.value)} delta={profit.changePercent}
            lines={[
              profit.marginPercent !== null && profit.marginPercent !== undefined && `Margin ${profit.marginPercent}% (was ${profit.previousMarginPercent ?? "—"}%)`,
              profit.costCoveragePercent !== null && profit.costCoveragePercent !== undefined && profit.costCoveragePercent < 100 && `Cost known for ${profit.costCoveragePercent}% of sales`,
            ]} />
          <KpiCard icon={ShoppingCart} label="Bills" loading={loading && !data} value={num(k.orders?.value)} delta={k.orders?.changePercent}
            lines={[data && `Avg outlet bill ${money(k.orders?.averageOrderValue)} (was ${money(k.orders?.previousAverageOrderValue)})`]} />
          <KpiCard icon={HandCoins} label="Money to collect" loading={loading && !data} value={money(k.receivables?.value)}
            lines={[data && `${plural(k.receivables?.bills, "unpaid bill")}`, data && `${money(k.receivables?.overdue30)} older than 30 days`]} />
          <KpiCard icon={ClipboardList} label="Purchases" loading={loading && !data} value={k.purchases ? money(k.purchases.value) : "—"} delta={k.purchases?.changePercent} deltaInverse
            lines={[k.purchases && plural(k.purchases.orders, "purchase order")]} />
          <KpiCard icon={Warehouse} label="Stock value" loading={loading && !data} value={k.inventoryValue ? money(k.inventoryValue.value) : "—"}
            lines={["Warehouse, at purchase cost", k.inventoryValue?.outletValueAtSellingPrice != null && `Outlets: ${money(k.inventoryValue.outletValueAtSellingPrice)} at selling price`]} />
        </div>

        {/* Alerts */}
        {data && (
          <Panel title="Needs attention" icon={AlertTriangle} subtitle="Issues found in this period, most urgent first.">
            {data.alerts?.length ? (
              <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                {data.alerts.map((alert) => {
                  const style = ALERT_STYLE[alert.level] || ALERT_STYLE.info;
                  const Icon = style.icon;
                  return (
                    <div key={alert.title} className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${style.box}`}>
                      <Icon size={18} className={`mt-0.5 shrink-0 ${style.text}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold ${style.text}`}>
                          <span className="mr-1.5 rounded bg-white/70 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">{style.label}</span>
                          {alert.title}
                        </p>
                        <p className="mt-0.5 text-sm text-slate-700">{alert.detail}</p>
                      </div>
                      {alert.link && (
                        <Link to={alert.link} className="shrink-0 text-xs font-semibold text-slate-700 underline">Open</Link>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-emerald-700">No issues found. Sales, margins, payments and stock all look healthy.</p>
            )}
          </Panel>
        )}

        {/* Trend + channels */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          <Panel title="Daily net sales" icon={TrendingUp} className="xl:col-span-2" subtitle="Hover or tap a day to see sales, profit and bills.">
            <TrendChart rows={data?.trend || []} />
          </Panel>
          <Panel title="Where sales come from" icon={Truck} subtitle={data?.notes?.netSales}>
            {data ? <ShareBars rows={channelRows} /> : <Empty>Loading…</Empty>}
          </Panel>
        </div>

        {/* Outlets + payments */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          <Panel title="Outlet performance" icon={Store} className="xl:col-span-2" subtitle="Net sales by location, with change against the previous period.">
            <Table
              rows={data?.outlets}
              empty="No outlet sales in this period."
              columns={[
                { key: "outlet", label: "Location", render: (r) => <span className="font-semibold text-slate-800">{r.outlet}</span> },
                { key: "revenue", label: "Net sales", right: true, render: (r) => money(r.revenue) },
                { key: "change", label: "Change", right: true, render: (r) => <Delta value={r.changePercent} suffix="" /> },
                { key: "bills", label: "Outlet bills", right: true, render: (r) => num(r.retailOrders) },
                { key: "abv", label: "Avg bill", right: true, render: (r) => (r.retailOrders ? money(r.retailAverageOrderValue) : "—") },
                { key: "b2b", label: "Dispatch", right: true, render: (r) => (r.b2bRevenue ? money(r.b2bRevenue) : "—") },
                { key: "profit", label: "Gross profit", right: true, render: (r) => money(r.grossProfit) },
                { key: "margin", label: "Margin", right: true, render: (r) => <MarginBadge value={r.marginPercent} /> },
              ]}
            />
          </Panel>
          <Panel title="How customers pay" icon={CircleDollarSign} subtitle="Outlet and online bills.">
            {data?.payments?.length ? <ShareBars rows={data.payments.map((p) => ({ ...p, label: p.channel }))} /> : <Empty>No payments in this period.</Empty>}
          </Panel>
        </div>

        {/* Products */}
        <Panel
          title="Products"
          icon={Boxes}
          subtitle="Line margin uses recorded purchase cost, or warehouse batch cost where none was recorded."
          action={
            <div className="flex rounded-lg border border-slate-200 p-0.5 text-sm">
              {[
                ["topByRevenue", "Top sellers"],
                ["topByProfit", "Most profit"],
                ["lowMargin", "Low margin"],
              ].map(([key, label]) => (
                <button key={key} onClick={() => setProductView(key)}
                  className={`rounded-md px-3 py-1 font-semibold ${productView === key ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                  {label}
                </button>
              ))}
            </div>
          }
        >
          <Table
            rows={productRows}
            empty={productView === "lowMargin" ? "No products below 5% margin. Pricing looks healthy." : "No product sales in this period."}
            columns={[
              { key: "rank", label: "#", render: (r) => <span className="text-slate-400">{productRows.indexOf(r) + 1}</span> },
              { key: "name", label: "Product", render: (r) => <ProductCell row={r} /> },
              { key: "qty", label: "Qty sold", right: true, render: (r) => num(r.qty) },
              { key: "revenue", label: "Sales", right: true, render: (r) => money(r.revenue) },
              { key: "profit", label: "Gross profit", right: true, render: (r) => (r.grossProfit === null ? "—" : money(r.grossProfit)) },
              { key: "margin", label: "Margin", right: true, render: (r) => <MarginBadge value={r.marginPercent} /> },
            ]}
          />
        </Panel>

        {/* Receivables + customers/purchases */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
          <Panel title="Money to collect" icon={HandCoins} className="xl:col-span-2" subtitle="Unpaid credit and Pay Later bills as of today, by age.">
            {receivables?.total ? (
              <>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {receivables.buckets.map((b) => (
                    <div key={b.key} className={`rounded-lg border px-3 py-2 ${b.key === "60+" && b.amount ? "border-rose-200 bg-rose-50" : b.key === "31-60" && b.amount ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                      <p className="text-xs font-semibold text-slate-500">{b.label}</p>
                      <p className="text-lg font-bold text-slate-900">{money(b.amount)}</p>
                      <p className="text-xs text-slate-500">{plural(b.bills, "bill")}</p>
                    </div>
                  ))}
                </div>
                <h3 className="mb-1 mt-4 text-sm font-bold text-slate-700">Largest balances</h3>
                <Table
                  rows={receivables.topDebtors}
                  columns={[
                    { key: "name", label: "Customer / buyer", render: (r) => (
                      <div><p className="font-medium text-slate-800">{r.name}</p><p className="text-xs text-slate-500">{[r.type, r.phone].filter(Boolean).join(" · ")}</p></div>
                    ) },
                    { key: "bills", label: "Bills", right: true, render: (r) => num(r.bills) },
                    { key: "oldest", label: "Oldest", right: true, render: (r) => `${r.oldestDays} days` },
                    { key: "amount", label: "Due", right: true, render: (r) => <b className="text-slate-900">{money(r.amount)}</b> },
                  ]}
                />
              </>
            ) : (
              <Empty>No unpaid bills.</Empty>
            )}
          </Panel>

          <div className="min-w-0 space-y-5">
            <Panel title="Customers" icon={Users} subtitle="Outlet and online customers identified by phone.">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-xl font-bold text-slate-900">{num(customers?.active)}</p><p className="text-xs text-slate-500">Active</p></div>
                <div><p className="text-xl font-bold text-slate-900">{num(customers?.new)}</p><p className="text-xs text-slate-500">New</p></div>
                <div><p className="text-xl font-bold text-slate-900">{customers ? `${customers.repeatPercent}%` : "—"}</p><p className="text-xs text-slate-500">Came back</p></div>
              </div>
              {customers && (
                <div className="mt-3 border-t border-slate-100 pt-2 text-center">
                  <Delta value={customers.changePercent} suffix="active customers vs previous period" />
                </div>
              )}
            </Panel>
            <Panel title="Top suppliers" icon={ClipboardList} subtitle="Purchase orders placed in this period.">
              <Table
                rows={purchases?.topSuppliers}
                empty="No purchase orders in this period."
                columns={[
                  { key: "supplier", label: "Supplier", render: (r) => <span className="font-medium text-slate-800">{r.supplier}</span> },
                  { key: "orders", label: "POs", right: true, render: (r) => num(r.orders) },
                  { key: "amount", label: "Amount", right: true, render: (r) => money(r.amount) },
                ]}
              />
            </Panel>
          </div>
        </div>

        {/* Stock health */}
        <Panel title="Stock health" icon={Warehouse} subtitle="Warehouse stock as of now, valued at purchase cost.">
          {warehouse ? (
            <>
              <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                {[
                  { label: "Products in stock", value: num(warehouse.inStockProducts), hint: `${num(warehouse.outOfStockProducts)} out of stock in warehouse${data?.stock?.outlet ? `, ${num(data.stock.outlet.outOfStock)} packs at outlets` : ""}` },
                  { label: "Running low", value: num(warehouse.lowCoverCount), hint: "Less than 7 days of stock left", tone: warehouse.lowCoverCount ? "amber" : "" },
                  { label: "Expiring in 30 days", value: money(warehouse.nearExpiry?.value), hint: `${plural(warehouse.nearExpiry?.batches, "batch", "batches")}${warehouse.expired?.batches ? ` · ${money(warehouse.expired.value)} already expired` : ""}`, tone: warehouse.expired?.value ? "rose" : warehouse.nearExpiry?.value ? "amber" : "" },
                  { label: "Not moved in 60 days", value: money(warehouse.deadStock?.value), hint: plural(warehouse.deadStock?.products, "product") },
                ].map((tile) => (
                  <div key={tile.label} className={`rounded-lg border px-3 py-2 ${tile.tone === "rose" ? "border-rose-200 bg-rose-50" : tile.tone === "amber" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
                    <p className="text-xs font-semibold text-slate-500">{tile.label}</p>
                    <p className="text-lg font-bold text-slate-900">{tile.value}</p>
                    <p className="text-xs text-slate-500">{tile.hint}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-3">
                <div>
                  <h3 className="mb-1 text-sm font-bold text-slate-700">Reorder soon</h3>
                  <Table
                    rows={warehouse.lowCover}
                    empty="Every fast-moving product has at least a week of stock."
                    columns={[
                      { key: "name", label: "Product", render: (r) => <ProductCell row={r} /> },
                      { key: "stock", label: "Stock", right: true, render: (r) => num(r.stock) },
                      { key: "perDay", label: "Per day", right: true, render: (r) => num(r.perDay) },
                      { key: "days", label: "Days left", right: true, render: (r) => <b className={r.daysCover < 3 ? "text-rose-700" : "text-amber-700"}>{num(r.daysCover)}</b> },
                    ]}
                  />
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-bold text-slate-700">Expiring or expired</h3>
                  <Table
                    rows={warehouse.expiringBatches}
                    empty="No batches expire in the next 30 days."
                    columns={[
                      { key: "name", label: "Product", render: (r) => <ProductCell row={r} /> },
                      { key: "exp", label: "Expiry", right: true, render: (r) => {
                        const expired = new Date(`${r.expDate}T23:59:59+05:30`) < new Date();
                        return <span className={expired ? "font-semibold text-rose-700" : "text-slate-700"}>{expired ? "Expired " : ""}{dayLabel(r.expDate)}</span>;
                      } },
                      { key: "value", label: "Value", right: true, render: (r) => money(r.value) },
                    ]}
                  />
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-bold text-slate-700">Slow-moving stock</h3>
                  <Table
                    rows={warehouse.deadStock?.top}
                    empty="All stock has moved in the last 60 days."
                    columns={[
                      { key: "name", label: "Product", render: (r) => <ProductCell row={r} /> },
                      { key: "stock", label: "Stock", right: true, render: (r) => num(r.stock) },
                      { key: "value", label: "Value", right: true, render: (r) => money(r.value) },
                    ]}
                  />
                </div>
              </div>
            </>
          ) : (
            <Empty>{loading ? "Loading…" : "Stock figures are not available."}</Empty>
          )}
        </Panel>

        {data?.generatedAt && (
          <p className="text-right text-xs text-slate-400">
            Updated {new Date(data.generatedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" })}
          </p>
        )}
      </div>
    </StockManagerLayout>
  );
}
